import React, { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from '@src/helpers/toaster'

import Popup from '@components/Popup'
import Button from '@components/Button'
import Text from '@components/Text'

import { useResendTeamInvite } from '@queries/OrgStructure'
import { requestErrorsHandler } from '@helpers/utils'

import type { InviteRef } from './utils'

export interface ResendInvitePopupProps {
  invite: InviteRef
  onClose(): void
}

// Confirmation modal for re-sending a pending or expired invite. Mirrors
// RevokeInvitePopup's structure so the two actions on the same row share one
// design language — the only difference is a non-destructive confirm button,
// since resending costs nothing but an email.
export const ResendInvitePopup: React.FC<ResendInvitePopupProps> = ({ invite, onClose }) => {
  const { t } = useTranslation('org', { keyPrefix: 'resendInvite' })
  const { mutateAsync: resend, isLoading } = useResendTeamInvite(invite.uuid)

  const handleConfirm = useCallback(async () => {
    try {
      await resend({})
      onClose()
      toast.success(t('successToast', { email: invite.email }))
    } catch (err) {
      // Pass the fallback copy INTO the handler rather than firing a second
      // toast: RequestErrorHandler.handle() already shows one, and uses this
      // string only when the response carries no message of its own.
      requestErrorsHandler(err, t('errorToast'))
    }
  }, [resend, onClose, t, invite.email])

  return (
    <Popup containerStyle="z-nested" className="p-6" onClose={onClose}>
      <div className="flex flex-col mb-4 gap-3">
        {/* ``<Text>`` renders a ``<div>`` of ``<p>`` elements; nesting it in a
            ``<p>`` is invalid HTML that browsers auto-close unpredictably.
            Same ``<div>`` wrappers as RevokeInvitePopup. */}
        <div className="font-medium border-b leading-8">
          <Text text={t('title')} />
        </div>
        <div>
          {/* ``<Trans>`` rather than ``<Text>``: the recipient is the one thing
              the admin must read before confirming, and ``<Text>`` only bolds
              whole space-separated tokens — the trailing "?" would swallow the
              marker. Indexed components match the convention elsewhere. */}
          <p>
            <Trans
              t={t}
              i18nKey="confirmText"
              values={{ email: invite.email }}
              components={[<strong className="font-semibold" key="0" />]}
            />
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="outlined" buttonType="button" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button color="primary" buttonType="button" onClick={handleConfirm} loading={isLoading}>
          {t('confirm')}
        </Button>
      </div>
    </Popup>
  )
}

export default ResendInvitePopup
